import { call, put, takeEvery } from "redux-saga/effects";
import { AnyAction } from "redux";
import Apis from "./apis";
import ActionTypes, {
    setGSCReportData,
    setDownloadGSCReportData,
    setGSCSavedViewsList,
    setCreateGSCSavedView,
    setUpdateGSCSavedView,
    setDeleteGSCSavedView,
    setGSCTenantDetails,
    getGSCSavedViewsList as getGSCSavedViewsListAction,
} from "./actions";
import { getContext } from "../../app/duck/context-container";
import { BackendViewDataType } from "./types";
import { getDownloadFileName, transformBackendViewData } from "./utils";

// tslint:disable-next-line:no-any
export function* fetchGSCReportData(action: AnyAction): any {
    yield put(setGSCReportData({ isLoading: true, error: null, result: null }));

    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.getGSCReportData,
        { tenantCode },
        action.payload
    );
    switch (response.status) {
        case 200:
            yield put(setGSCReportData({
                isLoading: false,
                error: null,
                result: response.data,
            }));
            break;
        case 204:
            yield put(setGSCReportData({
                isLoading: false,
                error: "No data found for selected filters",
                result: null,
            }));
            break;
        case 400:
            yield put(setGSCReportData({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line:no-any
export function* downloadGSCReportData(action: AnyAction): any {
    yield put(setDownloadGSCReportData({ isLoading: true, error: null, result: null }));
    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.downloadGSCReportData,
        { tenantCode },
        action.payload.filters
    );
    switch (response.status) {
        case 200:
            try {
                const filename = getDownloadFileName(
                    action.payload.viewName,
                    action.payload.filters.start_date,
                    action.payload.filters.end_date,
                );
                const blob = new Blob([response.data], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                yield put(setDownloadGSCReportData({
                    isLoading: false,
                    error: null,
                    result: "File downloaded successfully",
                }));
            } catch (error) {
                yield put(setDownloadGSCReportData({
                    isLoading: false,
                    error: "Error downloading file",
                    result: null,
                }));
            }
            break;
        case 204:
            yield put(setDownloadGSCReportData({
                isLoading: false,
                error: "No data found for selected filters",
                result: null,
            }));
            break;
        case 400:
            yield put(setDownloadGSCReportData({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: no-any
export function* getGSCSavedViewsList(): any {
    yield put(setGSCSavedViewsList({ isLoading: true, error: null, result: null }));

    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.getGSCSavedViewsList,
        { tenantCode },
    );
    switch (response.status) {
        case 200:
            const viewsData = response.data.map((view: BackendViewDataType) => transformBackendViewData(view));
            yield put(setGSCSavedViewsList({
                isLoading: false,
                error: null,
                result: viewsData,
            }));
            break;
        case 204:
            yield put(setGSCSavedViewsList({
                isLoading: false,
                error: "No views created",
                result: null,
            }));
            break;
        case 500:
            yield put(setGSCSavedViewsList({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: no-any
export function* getGSCTenantDetails(): any {
    yield put(setGSCTenantDetails({ isLoading: true, error: null, result: null }));
    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.getGSCTenantDetails,
        { tenantCode },
    );
    switch (response.status) {
        case 200:
            yield put(setGSCTenantDetails({
                isLoading: false,
                error: null,
                result: response.data,
            }));
            break;
        case 204:
            yield put(setGSCTenantDetails({
                isLoading: false,
                error: "No tenant details found",
                result: null,
            }));
            break;
        case 500:
            yield put(setGSCTenantDetails({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: no-any
export function* createGSCSavedView(action: AnyAction): any {
    yield put(setCreateGSCSavedView({ isLoading: true, error: null, result: null }));

    const { tenantCode = "" } = getContext();

    const response = yield call(
        Apis.createGSCSavedView,
        { tenantCode },
        action.payload
    );
    switch (response.status) {
        case 201:
            yield put(setCreateGSCSavedView({
                isLoading: false,
                error: null,
                result: transformBackendViewData(response.data),
            }));
            break;
        case 409:
            yield put(setCreateGSCSavedView({
                isLoading: false,
                error: "View with this name already exists.",
                result: null,
            }));
            break;
        case 204:
        case 400:
        case 500:
            yield put(setCreateGSCSavedView({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: no-any
export function* updateGSCSavedView(action: AnyAction): any {
    yield put(setUpdateGSCSavedView({ isLoading: true, error: null, result: null }));

    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.updateGSCSavedView,
        { tenantCode },
        action.payload.viewId,
        action.payload.payload
    );
    switch (response.status) {
        case 200:
            yield put(setUpdateGSCSavedView({
                isLoading: false,
                error: null,
                result: transformBackendViewData(response.data),
            }));
            break;
        case 204:
        case 400:
        case 500:
            yield put(setUpdateGSCSavedView({
                isLoading: false,
                error: "Something went wrong!",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: no-any
export function* deleteGSCSavedView(action: AnyAction): any {
    yield put(setDeleteGSCSavedView({ isLoading: true, error: null, result: null }));

    const { tenantCode = "" } = getContext();
    const response = yield call(
        Apis.deleteGSCSavedView,
        { tenantCode },
        action.payload.viewId
    );
    switch (response.status) {
        case 200:
            yield put(setDeleteGSCSavedView({
                isLoading: false,
                error: null,
                result: "View deleted successfully",
            }));
            yield put(getGSCSavedViewsListAction());
            break;
        case 204:
        case 400:
        case 500:
            yield put(setDeleteGSCSavedView({
                isLoading: false,
                error: "Error while deleting view",
                result: null,
            }));
            break;
        default:
            break;
    }
}

// tslint:disable-next-line: typedef
export function* watchGetGSCReportData() {
    yield takeEvery(ActionTypes.GET_GSC_REPORT_DATA, fetchGSCReportData);
}

// tslint:disable-next-line: typedef
export function* watchDownloadGSCReportData() {
    yield takeEvery(ActionTypes.DOWNLOAD_GSC_REPORT_DATA, downloadGSCReportData);
}

// tslint:disable-next-line: typedef
export function* watchGetGSCSavedViewsList() {
    yield takeEvery(ActionTypes.GET_GSC_SAVED_VIEWS_LIST, getGSCSavedViewsList);
}

// tslint:disable-next-line: typedef
export function* watchGetGSCTenantDetails() {
    yield takeEvery(ActionTypes.GET_GSC_TENANT_DETAILS, getGSCTenantDetails);
}

// tslint:disable-next-line: typedef
export function* watchCreateGSCSavedView() {
    yield takeEvery(ActionTypes.CREATE_GSC_SAVED_VIEW, createGSCSavedView);
}

// tslint:disable-next-line: typedef
export function* watchUpdateGSCSavedView() {
    yield takeEvery(ActionTypes.UPDATE_GSC_SAVED_VIEW, updateGSCSavedView);
}

// tslint:disable-next-line: typedef
export function* watchDeleteGSCSavedView() {
    yield takeEvery(ActionTypes.DELETE_GSC_SAVED_VIEW, deleteGSCSavedView);
}
